import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getWidgetConfig } from '../api';
import { ROUTES, type RouteName } from '../router';

const ONBOARDING_DISMISSED_KEY = 'hype_onboarding_dismissed';

interface WidgetStatus {
  key: string;
  label: string;
  route: RouteName;
  icon: string;
  description: string;
  enabled: boolean;
  loaded: boolean;
  error: boolean;
}

const WIDGETS = [
  {
    key: 'countdown',
    label: 'Countdown Timer',
    route: 'countdown' as RouteName,
    icon: '\u23F3',
    description: 'Create urgency with a countdown to sales, launches, or events.',
  },
  {
    key: 'sales_popup',
    label: 'Sales Popup',
    route: 'sales-popup' as RouteName,
    icon: '\uD83D\uDCE2',
    description: 'Show recent purchase notifications to build social proof.',
  },
  {
    key: 'stock_counter',
    label: 'Stock Counter',
    route: 'stock-counter' as RouteName,
    icon: '\uD83D\uDCE6',
    description: 'Display low-stock warnings to encourage faster decisions.',
  },
  {
    key: 'announcement_bar',
    label: 'Announcement Bar',
    route: 'announcement-bar' as RouteName,
    icon: '\uD83D\uDCCC',
    description: 'Pin messages for shipping deals, promos, or store updates.',
  },
  {
    key: 'visitor_counter',
    label: 'Visitor Counter',
    route: 'visitor-counter' as RouteName,
    icon: '\uD83D\uDC65',
    description: 'Show real-time visitor counts to create a sense of demand.',
  },
];

@customElement('hype-dashboard-page')
export class DashboardPage extends LitElement {
  override createRenderRoot() { return this; }

  @state() private _widgets: WidgetStatus[] = WIDGETS.map(w => ({
    ...w,
    enabled: false,
    loaded: false,
    error: false,
  }));

  @state() private _onboardingDismissed = localStorage.getItem(ONBOARDING_DISMISSED_KEY) === 'true';

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadWidgetStatuses();
  }

  private async _loadWidgetStatuses(): Promise<void> {
    const results = await Promise.allSettled(
      WIDGETS.map(async (w) => {
        const config = await getWidgetConfig(w.key);
        return { key: w.key, enabled: config.enabled };
      })
    );

    this._widgets = WIDGETS.map((w, i) => {
      const result = results[i];
      if (result.status === 'fulfilled') {
        return { ...w, enabled: result.value.enabled, loaded: true, error: false };
      }
      return { ...w, enabled: false, loaded: true, error: true };
    });
  }

  private get _activeCount(): number {
    return this._widgets.filter(w => w.loaded && !w.error && w.enabled).length;
  }

  private get _allLoaded(): boolean {
    return this._widgets.every(w => w.loaded);
  }

  private _renderStatCards() {
    const active = this._activeCount;
    const total = this._widgets.length;
    const analyticsRoute = ROUTES.find(r => r.name === 'analytics');
    const billingRoute = ROUTES.find(r => r.name === 'billing');

    return html`
      <s-grid gridTemplateColumns="'repeat(3, 1fr)'" gap="base">
        <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
          <s-stack gap="small-200">
            <s-text tone="subdued" variant="bodySm">Active widgets</s-text>
            ${this._allLoaded
              ? html`<s-text variant="headingLg" fontWeight="bold">${active} / ${total}</s-text>`
              : html`<s-spinner size="small"></s-spinner>`}
          </s-stack>
        </s-box>
        <a class="db-stat-link" href="${analyticsRoute?.path ?? '/analytics'}">
          <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
            <s-stack gap="small-200">
              <s-text tone="subdued" variant="bodySm">Performance</s-text>
              <s-text variant="headingSm" fontWeight="semibold">View Analytics</s-text>
            </s-stack>
          </s-box>
        </a>
        <a class="db-stat-link" href="${billingRoute?.path ?? '/billing'}">
          <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
            <s-stack gap="small-200">
              <s-text tone="subdued" variant="bodySm">Subscription</s-text>
              <s-text variant="headingSm" fontWeight="semibold">Manage Plan</s-text>
            </s-stack>
          </s-box>
        </a>
      </s-grid>
    `;
  }

  private _renderWidgetCard(widget: WidgetStatus, isLast: boolean) {
    const cardClass = isLast ? 'db-widget-card db-widget-card--full' : 'db-widget-card';

    if (!widget.loaded) {
      return html`
        <div class="${cardClass}">
          <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
            <s-stack gap="base" alignItems="start">
              <s-spinner size="small"></s-spinner>
              <s-text fontWeight="semibold">${widget.label}</s-text>
            </s-stack>
          </s-box>
        </div>
      `;
    }

    if (widget.error) {
      return html`
        <div class="${cardClass}">
          <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
            <s-stack gap="base">
              <span class="db-widget-icon">${widget.icon}</span>
              <s-stack gap="small-200">
                <s-text fontWeight="semibold">${widget.label}</s-text>
                <s-badge tone="critical">Failed to load</s-badge>
              </s-stack>
            </s-stack>
          </s-box>
        </div>
      `;
    }

    const route = ROUTES.find(r => r.name === widget.route);
    return html`
      <a class="db-card-link ${cardClass}" href="${route?.path ?? '/'}">
        <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
          <s-stack gap="base">
            <span class="db-widget-icon">${widget.icon}</span>
            <s-stack gap="small-200">
              <s-stack direction="inline" gap="small" alignItems="center">
                <s-text fontWeight="semibold">${widget.label}</s-text>
                ${widget.enabled
                  ? html`<s-badge tone="success">Active</s-badge>`
                  : html`<s-badge>Off</s-badge>`}
              </s-stack>
              <s-text tone="subdued" variant="bodySm">${widget.description}</s-text>
            </s-stack>
          </s-stack>
        </s-box>
      </a>
    `;
  }

  private _dismissOnboarding() {
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true');
    this._onboardingDismissed = true;
  }

  private _renderGetStartedBanner() {
    if (!this._allLoaded) return null;
    if (this._activeCount > 0) return null;
    if (this._onboardingDismissed) return null;

    return html`
      <s-banner
        status="info"
        title="Welcome to Hype!"
        @dismiss=${() => this._dismissOnboarding()}
      >
        <p>Enable your first widget to start driving urgency on your store. Click any widget card below to configure and activate it — each takes less than a minute to set up.</p>
      </s-banner>
    `;
  }

  render() {
    const widgetCount = this._widgets.length;

    return html`
      <style>
        hype-dashboard-page .db-card-link {
          text-decoration: none;
          color: inherit;
          display: block;
          height: 100%;
          border-radius: var(--s-border-radius-base, 8px);
          transition: transform 150ms ease, box-shadow 150ms ease;
        }
        hype-dashboard-page .db-card-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        hype-dashboard-page .db-card-link:active {
          transform: translateY(0);
          box-shadow: none;
        }
        hype-dashboard-page .db-stat-link {
          text-decoration: none;
          color: inherit;
          display: block;
          height: 100%;
          border-radius: var(--s-border-radius-base, 8px);
          transition: transform 150ms ease, box-shadow 150ms ease;
        }
        hype-dashboard-page .db-stat-link:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        hype-dashboard-page .db-widget-icon {
          font-size: 24px;
          line-height: 1;
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: var(--s-color-bg-surface-secondary, #f6f6f7);
        }
        hype-dashboard-page .db-widget-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        hype-dashboard-page .db-widget-card--full {
          grid-column: 1 / -1;
          max-width: calc(50% - 8px);
        }
        @media (max-width: 640px) {
          hype-dashboard-page .db-widget-grid {
            grid-template-columns: 1fr;
          }
          hype-dashboard-page .db-widget-card--full {
            max-width: none;
          }
          hype-dashboard-page s-grid[gridTemplateColumns] {
            display: grid !important;
            grid-template-columns: 1fr !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          hype-dashboard-page .db-card-link,
          hype-dashboard-page .db-stat-link {
            transition: none;
          }
        }
      </style>
      <s-page heading="Dashboard">
        <s-text tone="subdued">Overview of your Hype Widgets installation.</s-text>

        ${this._renderStatCards()}
        ${this._renderGetStartedBanner()}

        <s-section heading="Widgets">
          <div class="db-widget-grid">
            ${this._widgets.map((w, i) =>
              this._renderWidgetCard(w, widgetCount % 2 !== 0 && i === widgetCount - 1)
            )}
          </div>
        </s-section>
      </s-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hype-dashboard-page': DashboardPage;
  }
}
